import * as t from 'io-ts';

import {ConversationSortPropertiesEnum} from './conversationCommonTypes';
import {parseConversationHighlight, RConversationHighlight} from './conversationHighlightModel';
import {parseConversationStatus, parseTrackers, RConversationBase} from './conversationModel';
import {parseConversationSafetyNet} from './conversationSafetyNetModel';
import {parseDraftSeed, RDraftSeed} from './draftSeedModel';
import {MessageTypesEnum, parseMessageType} from './messageModel';
import {parseRecipient} from './recipientModel';
import {parseReminder} from './reminderModel';

const RConversationSearchResultCommentSummary = t.readonly(
  t.type({
    text: t.string,
    authorId: t.number,
  }),
  'conversationSearchResultCommentSummary',
);

const RConversationSearchResultMessageDraft = t.readonly(
  t.type({
    blurb: t.string,
    authorId: t.number,
    isShared: t.boolean,
  }),
  'conversationSearchResultMessageDraft',
);

export const RConversationSearchResult = t.readonly(
  t.intersection([
    RConversationBase,
    t.type({
      // Teammate ids that have read the conversation.
      readByIds: t.readonlyArray(t.number),
      teamUnsnoozedSeenByIds: t.readonlyArray(t.number),

      // The following fields control what will be displayed on the third line of the conversation cell.
      // The backend will try to minimize the number of SQL requests and, as such, depending on what
      // matched, they can be undefined/empty following their display priority.
      // They can also be overriden client-side by a more recent ConversationModel, if available.
      // For more information see ConversationSearchResultCellContainer and CellActivity.

      // Combined highlights of all the submatches.
      mergedHighlights: t.union([RConversationHighlight, t.undefined]),

      // The last visible message draft by the requester.
      lastMessageDraft: t.union([RConversationSearchResultMessageDraft, t.undefined]),

      // The message drafts not visible by the requester.
      messageDraftSeeds: t.readonlyArray(RDraftSeed),

      // The requester's comment draft, if it exists.
      commentDraft: t.union([RConversationSearchResultCommentSummary, t.undefined]),

      // Defined when we didn't match on an activity and the last activity is a comment.
      commentSummary: t.union([RConversationSearchResultCommentSummary, t.undefined]),
    }),
  ]),
  'conversationSearchResult',
);

/*
 * Types.
 */

export interface ConversationSearchResultModel extends t.TypeOf<typeof RConversationSearchResult> {}
export interface ConversationSearchResultCommentSummaryModel
  extends t.TypeOf<typeof RConversationSearchResultCommentSummary> {}
export interface ConversationSearchResultMessageDraftModel
  extends t.TypeOf<typeof RConversationSearchResultMessageDraft> {}

/*
 * IO methods.
 */

function parseConversationSearchResultCommentSummary(src: any): ConversationSearchResultCommentSummaryModel {
  return {authorId: src.author_id, text: src.text};
}

function parseConversationSearchResultMessageDraft(src: any): ConversationSearchResultMessageDraftModel {
  return {authorId: src.author_id, blurb: src.blurb, isShared: src.is_shared};
}

export function parseConversationSearchResult(src: any): ConversationSearchResultModel {
  const bumpedAt = src.orders.bumped_at ?? Date.now(); // FIXME(ldemarne) ?? -> attachments
  const waitingSince = src.orders.waiting_since || bumpedAt;
  const lastActivityDate = src.orders.last_activity_at || bumpedAt;
  const lastMessageOrCommentDate = src.orders.last_comment_or_reply_at || lastActivityDate;

  return {
    id: src.conversation_id,
    url: src.url,
    namespace: src.namespace,
    numMessages: src.num_messages,
    teamStatus: parseConversationStatus(src),
    recipient: (src.contact && parseRecipient(src.contact)) || undefined,
    subject: src.subject,
    messageType: parseMessageType(src.message_type) || MessageTypesEnum.INTERNAL,
    hasAttachments: src.has_attachments,
    updatedAt: src.updated_at,
    assigneeId: src.assignee_id || undefined,
    inboxIds: src.inbox_ids,
    channelIds: src.channel_ids,
    tagIds: src.tag_ids,
    readByIds: src.read_by_ids,
    teamUnsnoozedSeenByIds: src.team_unsnoozed_seen_by_ids ?? [],
    safetyNets: src.safety_nets?.map(parseConversationSafetyNet) ?? undefined,
    trackers: parseTrackers(src.trackers, undefined),
    orders: {
      [ConversationSortPropertiesEnum.BUMPED_AT]: bumpedAt,
      [ConversationSortPropertiesEnum.WAITING_SINCE]: waitingSince,
      [ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE]: lastActivityDate,
      [ConversationSortPropertiesEnum.LAST_MESSAGE_OR_COMMENT_DATE]: lastMessageOrCommentDate,
    },
    mergedHighlights: parseConversationHighlight(src.merged_highlights),
    summary: src.summary ?? '',
    topicIds: src.topic_ids,
    reminders: src.reminders && src.reminders.map(parseReminder),
    canceledReminders: (src.canceled_reminders && src.canceled_reminders.map(parseReminder)) || [],
    isUnsnoozed: src.unsnoozed ?? false,
    seenAt: undefined,
    commentSummary: src.comment_summary
      ? parseConversationSearchResultCommentSummary(src.comment_summary)
      : undefined,
    commentDraft: src.comment_draft
      ? parseConversationSearchResultCommentSummary(src.comment_draft)
      : undefined,
    lastMessageDraft: src.last_message_draft
      ? parseConversationSearchResultMessageDraft(src.last_message_draft)
      : undefined,
    messageDraftSeeds: src.message_draft_seeds?.map(parseDraftSeed) ?? [],
  };
}
